import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';
import {v4 as uuidv4} from 'uuid';

// Need this to allow uuid to work in environments where getRandomValues()
// is not supported.
import 'react-native-get-random-values';

import apiService from '../transport/apiService';
import {
  getValueFromStorage,
  removeValueFromStorage,
  setValueToStorage,
} from '../utils/localStorage';
import {logger} from '../utils/logger';

import type {RootStore} from './rootStore';

const DEVICE_ID_STORAGE_KEY = 'DEVICE_ID_STORAGE_KEY';

export class GlobalStore {
  rootStore: RootStore;

  isAppInitialising = true;
  isUserDataInitialising = false;
  isSwitchingStore = false;
  isAccountRoute = false;
  isInformationRoute = false;
  isHomeRoute = false;
  wasStoreSelectedOnAppStart = true;

  /** App version enforcement - true if current app version is below minimum required */
  requiresUpgrade = false;
  /** Current app version (set during initialization) */
  currentAppVersion: string | null = null;
  /** Minimum required version from server (for display purposes) */
  minimumRequiredVersion: string | null = null;

  deviceId: string | null = null;
  apiAvailable = false;
  showInstoreOnly = false;
  acceptedInStoreOnly = false;
  filtersVisible = false;
  filterPanelDefaultTab: 'SORT' | 'FILTER' = 'SORT';

  isFromNavigationSearch = false;

  // HEADER VARIABLES
  safeAreaColour = 'white';

  // Universal link handling. We need to wait for stores to be loaded and, in some cases, a
  // shop to be selected, before navigating to the universal/deep link.
  delayedNavigationPath: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    this.reset();
    makeLoggable(this);
  }

  setWasStoreSelectedOnAppStart = (wasStoreSelectedOnAppStart: boolean) => {
    this.wasStoreSelectedOnAppStart = wasStoreSelectedOnAppStart;
  };
  setIsUserDataInitialising = (isUserDataInitialising: boolean) => {
    this.isUserDataInitialising = isUserDataInitialising;
  };
  setIsAppInitialising = (isAppInitialising: boolean) => {
    this.isAppInitialising = isAppInitialising;
  };

  setIsAccountRoute = (isAccountRoute: boolean) => {
    this.isAccountRoute = isAccountRoute;
  };

  setIsInformationRoute = (isInformationRoute: boolean) => {
    this.isInformationRoute = isInformationRoute;
  };

  setIsHomeRoute = (isHomeRoute: boolean) => {
    this.isHomeRoute = isHomeRoute;
  };

  setIsSwitchingStore = (isSwitchingStore: boolean) => {
    this.isSwitchingStore = isSwitchingStore;
  };

  setRequiresUpgrade = (
    requiresUpgrade: boolean,
    currentVersion?: string,
    minimumVersion?: string,
  ) => {
    this.requiresUpgrade = requiresUpgrade;
    if (currentVersion) {
      this.currentAppVersion = currentVersion;
    }
    if (minimumVersion) {
      this.minimumRequiredVersion = minimumVersion;
    }
  };

  setDelayedNavigationPath = (path: string) => {
    runInAction(() => (this.delayedNavigationPath = path));
  };

  clearDelayedNavigationPath = () => {
    runInAction(() => (this.delayedNavigationPath = null));
  };

  setIsFromNavigationSearch = (value: boolean) => {
    this.isFromNavigationSearch = value;
  };

  reset = () => {
    this.acceptedInstoreShopping(false);
    this.clearDelayedNavigationPath();
  };

  pingApi = async () => {
    const apiUp = await apiService.pingApi();
    runInAction(() => (this.apiAvailable = apiUp));
    return apiUp;
  };

  getDeviceIdFromStorage = async () => getValueFromStorage(DEVICE_ID_STORAGE_KEY);

  setDeviceIdToStorage = (deviceId: string) => setValueToStorage(DEVICE_ID_STORAGE_KEY, deviceId);

  // Storage calls
  loadDeviceIdFromLocalStoreOrGenerateNew = async () => {
    let deviceId: string | null = null;
    try {
      deviceId = await this.getDeviceIdFromStorage();
      if (!deviceId) {
        // Storage didn't have the key, so create a new one.
        deviceId = uuidv4();
        this.setDeviceIdToStorage(deviceId);
      }
    } catch (error: unknown) {
      logger.warn('GlobalStore', 'Problem reading device ID from storage, generating new one', {
        error,
      });
      deviceId = uuidv4();
    } finally {
      runInAction(() => (this.deviceId = deviceId));
    }
  };

  clearDeviceId = async () => {
    try {
      await removeValueFromStorage(DEVICE_ID_STORAGE_KEY);
      runInAction(() => (this.deviceId = null));
    } catch (error: unknown) {
      logger.warn('GlobalStore', 'Problem clearing device ID from storage', {
        errorCode: (error as Record<string, unknown>)?.code,
        error,
      });
    }
  };

  resetDeviceId = async () => {
    await this.clearDeviceId();
    await this.loadDeviceIdFromLocalStoreOrGenerateNew();
    return null;
  };

  acceptedInstoreShopping = (value: boolean) => {
    this.acceptedInStoreOnly = value;
  };

  setInstoreOnlyVisible = (value: boolean) => {
    this.showInstoreOnly = value;
  };

  setFiltersVisible = (value: boolean) => {
    this.filtersVisible = value;
  };

  setFilterPanelDefaultTab = (tab: 'SORT' | 'FILTER') => {
    this.filterPanelDefaultTab = tab;
  };
}
